/* eslint-disable no-restricted-syntax */

/* Above comment silences warnings related to 'course' and 'upsell' language in strings */
import _t from 'i18n!nls/degree-description';

export type PersonalizedMessageComponent = 'EMBEDDED' | 'TOOLTIP' | 'MODAL';
export type PersonalizedMessageActionType = 'ENROLL_APPLY' | 'REQUEST_INFO';

export type PersonalizedMessage = {
  id: PersonalizedMessageName;
  priority: number;
  component: PersonalizedMessageComponent;
  title: string;
  body: string;
  ctaText: string;
  ctaActionType: PersonalizedMessageActionType;
};

/**
 * These are all the possible personalized messages that can be shown on a DDP.
 * Some messages require extra info to be used, and some can be used on their own. They all need to be functions
 * to properly show a translated message on SSR.
 *
 * Example usage:
 * `const message = personalizedMessages.viewedDdpSubPages('Admissions');`
 * If you're using Message as a variable:
 * `const message = personalizedMessages[message]('Admissions');`
 */
export const personalizedMessages = {
  enrolledStackableRelatedTo: (stackableItemName: string): PersonalizedMessage => ({
    id: 'enrolledStackableRelatedTo',
    priority: 1,
    component: 'EMBEDDED' as const,
    title: _t('You’re already enrolled in a related course! 🚀'),
    body: _t(
      'Did you know the content you’re enrolled in, #{courseraContentItemName}, is related to this degree? You may have already started learning about the subjects taught in this degree program! Continue your learning journey in this field by starting an application now.',
      {
        courseraContentItemName: stackableItemName,
      }
    ),
    ctaText: _t('View application now'),
    ctaActionType: 'ENROLL_APPLY' as const,
  }),
  enrolledStackableOffersCredit: (
    stackableItemName: string,
    degreeName: string,
    partnerName: string
  ): PersonalizedMessage => ({
    id: 'enrolledStackableOffersCredit',
    priority: 2,
    component: 'EMBEDDED' as const,
    title: _t('You’re already enrolled in content that could get you credit towards this degree! 🚀'),
    body: _t(
      'When you complete #{courseraContentItemName} you can earn college credit if you apply & are admitted into the #{degreeName} from #{partnerName}. Continue your momentum! Start an application now to unlock the next phase of your career.',
      {
        courseraContentItemName: stackableItemName,
        degreeName,
        partnerName,
      }
    ),
    ctaText: _t('View application now'),
    ctaActionType: 'ENROLL_APPLY' as const,
  }),
  enrolledStackablePartOf: (stackableItemName: string): PersonalizedMessage => ({
    id: 'enrolledStackablePartOf',
    priority: 3,
    component: 'EMBEDDED' as const,
    title: _t('You’re already enrolled in content that could get you credit towards this degree! 🚀'),
    body: _t(
      'You’re in #{courseraContentItemName}, which is part of this degree. If you apply & are admitted to the degree, your coursework will count towards your degree learning and all of your progress will transfer with you. Continue your momentum! Start an application now to unlock the next phase of your career.',
      {
        courseraContentItemName: stackableItemName,
      }
    ),
    ctaText: _t('View application now'),
    ctaActionType: 'ENROLL_APPLY' as const,
  }),
  viewedDdpTwiceMax: (): PersonalizedMessage => ({
    id: 'viewedDdpTwiceMax',
    priority: 4,
    component: 'EMBEDDED' as const,
    title: _t('Welcome back! 🎉'),
    body: _t(
      'Our enrollment experts are ready to help you make an informed decision about this degree. Request info and start your application now.'
    ),
    ctaText: _t('Request info now'),
    ctaActionType: 'REQUEST_INFO' as const,
  }),
  viewedDdpAboveTwice: (): PersonalizedMessage => ({
    id: 'viewedDdpAboveTwice',
    priority: 5,
    component: 'EMBEDDED' as const,
    title: _t('Welcome back! 🎉'),
    body: _t(
      'We’re glad that you’re interested in this program. Take 30 seconds to request more info now and get insights from Coursera’s enrollment experts so you can make an informed decision.'
    ),
    ctaText: _t('Request info now'),
    ctaActionType: 'REQUEST_INFO' as const,
  }),
  viewedDdpSubPages: (mostViewedTab: string): PersonalizedMessage => ({
    id: 'viewedDdpSubPages',
    priority: 6,
    component: 'EMBEDDED' as const,
    title: _t('Welcome back! 🎉'),
    body: _t(
      'We’re glad that you’re interested in learning more about this degree’s #{mostViewedSubPage}. Get started now!',
      {
        mostViewedSubPage: mostViewedTab,
      }
    ),
    ctaText: _t('Request info now'),
    ctaActionType: 'REQUEST_INFO' as const,
  }),
  clickedEoi: (): PersonalizedMessage => ({
    id: 'clickedEoi',
    priority: 7,
    component: 'EMBEDDED' as const,
    title: _t('You’re so close to getting your questions answered! 🚀'),
    body: _t('Finish your request for info on this degree so we can send you more details.'),
    ctaText: _t('Request info now'),
    ctaActionType: 'REQUEST_INFO' as const,
  }),
  submittedEoi: (): PersonalizedMessage => ({
    id: 'submittedEoi',
    priority: 8,
    component: 'TOOLTIP' as const,
    title: _t('Hi again! 👋'),
    body: _t(
      'Now that you’ve requested more info on this degree, continue your degree journey by starting your application now.'
    ),
    ctaText: _t('View application now'),
    ctaActionType: 'ENROLL_APPLY' as const,
  }),
  clickedApply: (): PersonalizedMessage => ({
    id: 'clickedApply',
    priority: 9,
    component: 'TOOLTIP' as const,
    title: _t('Hi again! 👋'),
    body: _t(
      'Now that you’ve checked out this application, keep your momentum going by starting your application now.'
    ),
    ctaText: _t('View application now'),
    ctaActionType: 'ENROLL_APPLY' as const,
  }),
  startedApply: (deadline: string): PersonalizedMessage => ({
    id: 'startedApply',
    priority: 10,
    component: 'MODAL' as const,
    title: _t('You’re so close to the finish line! 🚀'),
    body: _t(
      'Congratulations on starting your application! Submit your application by #{applicationSubmitDeadlineDate} to be considered for this program’s upcoming cohort.',
      {
        applicationSubmitDeadlineDate: deadline,
      }
    ),
    ctaText: _t('Continue your application now'),
    ctaActionType: 'ENROLL_APPLY' as const,
  }),
} as const;

export type PersonalizedMessageName = keyof typeof personalizedMessages;

export type PersonalizedMessageFunc<T extends string[]> = (...args: T) => PersonalizedMessage;

export type PersonalizedMessageList = Record<PersonalizedMessageName, PersonalizedMessageFunc<string[]>>;
